<template>
  <v-row class="align-center">
    <v-col class="d-flex align-center" :cols="2">
      <div class="d-flex flex-column mr-1">
        <v-icon size="small" @click="$emit('rowUp', rowNum)">mdi-menu-up</v-icon>
        <v-icon size="small" @click="$emit('rowDown', rowNum)">mdi-menu-down</v-icon>
      </div>
      <MegaSelect
        :row="rowNum"
        :col="0"
        :options="coverPanelTypes"
        :displayValue="true"
        v-model:data="data.coverPanelType"
        @change="onTypeChange"
      ></MegaSelect>
    </v-col>
    <v-col :cols="1" class="d-flex">
      <MegaCell v-model:data="data.itemNum" :row="rowNum" :col="1" dataType="String"></MegaCell>
      <v-tooltip v-if="Object.keys(tips).includes(data.material)" location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-1" v-bind="props">mdi-information</v-icon>
        </template>
        <span>{{ tips[data.material] }}</span>
      </v-tooltip>
    </v-col>
    <v-col :cols="2" class="d-flex align-center">
      <div
        v-if="data.material"
        class="colour mr-2"
        :style="{ 'background-color': findCoverPanelHex(data.material) }"
      ></div>
      <MegaSelect
        :row="rowNum"
        :col="2"
        :options="filteredMaterials"
        v-model:data="data.material"
        placeholder="Select material"
        noOptions="No materials created yet"
      ></MegaSelect>
    </v-col>
    <v-col cols="1" class="d-flex">
      <v-icon
        :disabled="noLightingCoverPanel"
        :color="hasLightingSet ? 'info' : undefined"
        @click="lightingDialog = true"
        >mdi-lightbulb-on</v-icon
      >
      <v-icon
        :disabled="ventDialogDisabled"
        :color="hasVentSet ? 'info' : undefined"
        @click="ventDialog = true"
        class="ml-2 pt-1"
        >mdi-fan</v-icon
      >
      <v-icon
        v-if="data.coverPanelType === 'back' && grainRequired"
        :color="hasOpposingGrainSet ? 'info' : undefined"
        @click="grainDialog = true"
        class="ml-2 pt-1"
        >mdi-waves</v-icon
      >
      <v-icon
        :disabled="!hasEdgeDialog"
        :color="hasEdgesSet ? 'info' : undefined"
        @click="edgeDialog = true"
        class="ml-2 pt-1"
        >mdi-border-all</v-icon
      >
    </v-col>
    <v-col :cols="1">
      <MegaCell
        v-model:data="data.w"
        :row="rowNum"
        :col="3"
        :unit="region == 'us' ? 'inch' : 'mm'"
        :max="data.h > shortLengthMax || requiresVerticalGrain ? shortLengthMax : longLengthMax"
      ></MegaCell>
    </v-col>
    <v-col :cols="1">
      <MegaCell
        v-model:data="data.h"
        :row="rowNum"
        :col="4"
        :unit="region == 'us' ? 'inch' : 'mm'"
        :max="data.w > shortLengthMax ? shortLengthMax : longLengthMax"
      ></MegaCell>
    </v-col>
    <v-col :cols="2">
      <v-checkbox
        class="text-caption mt-0 pt-0"
        v-model="data.doubleSided"
        label="Double Sided"
        v-if="hasGrain"
        :disabled="lacquerCheckDisabled"
        hide-details
      >
        <template v-slot:label>
          <span class="text-caption">Double Sided</span>
        </template>
      </v-checkbox>
      <div v-else class="text-caption">n/a</div>
    </v-col>
    <v-col :cols="1">
      <MegaCell v-model:data="data.qty" :row="rowNum" :col="5"></MegaCell>
    </v-col>
    <v-col class="d-flex" :order="wonky ? 'first' : 'last'" :class="[{ 'justify-end': !wonky }]">
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-2" @click="$emit('copy', rowNum)" v-bind="props">mdi-content-copy</v-icon>
        </template>
        <span>Copy</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" class="mr-2" @click="$emit('copyBottom', rowNum)" v-bind="props"
            >mdi-arrow-collapse-down</v-icon
          >
        </template>
        <span>Copy to bottom</span>
      </v-tooltip>
      <v-tooltip location="top" :open-delay="500">
        <template v-slot:activator="{ props }">
          <v-icon size="small" @click="$emit('delete', rowNum)" v-bind="props">mdi-delete</v-icon>
        </template>
        <span>Delete</span>
      </v-tooltip>
    </v-col>

    <!-- Lighting Dialog -->
    <v-dialog v-model="lightingDialog" width="600">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Lighting</v-card-title>
        <v-card-text class="pt-4">
          <p>
            Dimension should be inputted as the total sum of all lightings put together eg: "800" for 2 lights of 400.
            <br />
            Total width of must be at least 60mm less than Cover Panel width (input is disabled without width).
            <br />
            You can enter an offset dimension and the choose which side to offset from.
          </p>
          <v-form ref="form">
            <v-text-field
              type="number"
              :rules="[
                (v) => (parseInt(v) < data.w - 60 && parseInt(v) >= 200) || 'Must be less than width minus 60mm'
              ]"
              :disabled="!data.w"
              v-model.number="data.lighting"
              placeholder="Sum Total Light Dimension"
            ></v-text-field>
            <v-radio-group :disabled="!data.w || !data.lighting" v-model="data.lightingRef">
              <v-radio label="From the right" value="r"></v-radio>
              <v-radio label="From the left" value="l"></v-radio>
              <v-radio @click="data.lightingOffset = null" label="Centered" value="c"></v-radio>
            </v-radio-group>
            <v-text-field
              type="number"
              :rules="[
                (v) =>
                  (parseInt(v) <= data.w - data.lighting - 60 && parseInt(v) >= 30) ||
                  (data.lightingRef == 'c' && !data.lightingOffset) ||
                  'Must be inset by at least 30mm from each end of the panel and not oversized offset.'
              ]"
              :disabled="!data.w || !data.lighting || !data.lightingRef || data.lightingRef == 'c'"
              v-model.number="data.lightingOffset"
              placeholder="Offset Dimension"
            ></v-text-field>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Vent Dialog -->
    <v-dialog v-model="ventDialog" width="600" persistent>
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Vented Plinth</v-card-title>
        <v-card-text class="pt-4">
          <p>
            Enter the dimension of the vent [should be 100mm less than carcass above], then enter the offset dimension
            and from which side of the plinth to offset. (input is disabled without width).
          </p>
          <v-form ref="ventForm">
            <v-text-field
              type="number"
              :rules="[
                (v) => !!v || v === 0 || 'Vent dimension is required.',
                (v) =>
                  (parseInt(v) <= data.w - 100 && parseInt(v) >= 50) ||
                  'Must be at least 50mm and less than width minus 100mm and not oversized offset.'
              ]"
              :disabled="!data.w"
              v-model.number="data.vent"
              placeholder="Vent Dimension"
            ></v-text-field>
            <v-radio-group
              :disabled="!data.w || !data.vent"
              v-model="data.ventRef"
              :rules="[(v) => !!v || 'Offset side is required.']"
            >
              <v-radio label="From the right" value="r"></v-radio>
              <v-radio label="From the left" value="l"></v-radio>
            </v-radio-group>
            <v-text-field
              type="number"
              :rules="[
                (v) => !!v || v === 0 || 'Offset dimension is required.',
                (v) =>
                  (parseInt(v) <= data.w - data.vent - 50 && parseInt(v) >= 50) ||
                  'Must be inset by at least 50mm from each end of plinth.'
              ]"
              :disabled="!data.w || !data.vent || !data.ventRef"
              v-model.number="data.ventOffset"
              placeholder="Offset Dimension"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancelVentDialog">Cancel</v-btn>
          <v-btn color="primary" @click="saveVentDialog">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Grain Direction Dialog -->
    <v-dialog v-model="grainDialog" width="600">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Grain Direction</v-card-title>
        <v-card-text class="pt-4">
          <p class="font-weight-bold">
            Default Grain is = {{ grainDirection ? Object.values(grainDirection)[0] : 'N/A' }}
          </p>
          <p>
            Make this item have an opposing grain direction to the default.
            <v-switch
              v-model="data.opposingGrain"
              :label="
                data.opposingGrain
                  ? Object.values(grainDirection)[0] == 'Horizontal Grain'
                    ? 'Opposing Grain is = Vertical Grain'
                    : 'Opposing Grain is = Horizontal Grain'
                  : 'Default Grain'
              "
              :disabled="onlyHozAllowed || onlyVertAllowed"
            ></v-switch>
          </p>
          <p v-if="onlyHozAllowed">
            WARNING! <br />
            Item is too wide; it's not possible to have a Vertical grain.
          </p>
          <p v-if="onlyVertAllowed">
            WARNING! <br />
            Item is too tall; it's not possible to have a Horizontal grain.
          </p>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Edge Treatment Dialog -->
    <v-dialog v-model="edgeDialog" width="500">
      <v-card>
        <v-card-title class="bg-primary text-white text-h6">Edge Banding</v-card-title>
        <v-card-text class="pt-4" color="primary">
          <v-switch v-model="edgeBandingEnabled" label="Enable edge-banding" class="mb-4" hide-details></v-switch>

          <div class="edge-box" v-if="data.edges">
            <div class="outside-label">Outside Face</div>

            <!-- Lacquer/finished-edge guide — band the same edge -->
            <div class="lacquer-guide guide-left" v-if="lacquerGuides.left"></div>
            <div class="lacquer-guide guide-right" v-if="lacquerGuides.right"></div>
            <div class="lacquer-guide guide-top" v-if="lacquerGuides.top"></div>
            <div class="lacquer-guide guide-bottom" v-if="lacquerGuides.bottom"></div>

            <v-checkbox
              v-model="data.edges.top"
              class="edge-top"
              hide-details
              :disabled="!edgeBandingEnabled"
            ></v-checkbox>
            <v-checkbox
              v-model="data.edges.bottom"
              class="edge-bottom"
              hide-details
              :disabled="!edgeBandingEnabled"
            ></v-checkbox>
            <v-checkbox
              v-model="data.edges.left"
              class="edge-left"
              hide-details
              :disabled="!edgeBandingEnabled"
            ></v-checkbox>
            <v-checkbox
              v-model="data.edges.right"
              class="edge-right"
              hide-details
              :disabled="!edgeBandingEnabled"
            ></v-checkbox>

            <span class="dim-width"
              >W: {{ region === 'us' ? parseFloat($utils.mmToInch(data.w)) + '"' : data.w + 'mm' }}</span
            >
            <span class="dim-height"
              >H: {{ region === 'us' ? parseFloat($utils.mmToInch(data.h)) + '"' : data.h + 'mm' }}</span
            >
          </div>

          <div class="lacquer-legend" v-if="hasLacquerGuide">
            <span class="lacquer-swatch"></span>
            <span>
              Red dashed line = lacquered/finished edge
              <br />
              — pick the same edge to be edge-banded.
            </span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import productData from '@/js/productData'
import { db } from '@/js/db'
import { doc, getDoc } from 'firebase/firestore'

import MegaSelect from '@/components/quoting/items/MegaSelect'
import MegaCell from '@/components/quoting/items/MegaCell'

export default {
  name: 'CoverPanel',
  props: ['rowNum', 'data', 'materials', 'wonky', 'region', 'location', 'requiresVerticalGrain', 'grainDirection'],
  components: { MegaCell, MegaSelect },
  data() {
    return {
      usShortMax: 2388, //96" which allows 94"
      usLongMax: 2998, //120" which allows 118"
      euLongMax: 2400, //EU sheet length, only warn (red border) when over 2400mm
      twoInchesInMM: 52,
      coverPanelTypes: productData.coverPanelTypes,
      lightingDialog: false,
      ventDialog: false,
      ventBackup: null,
      grainDialog: false,
      edgeDialog: false,
      shippingZone: ''
    }
  },
  async created() {
    await this.getShippingZoneName(this.location)
    // console.log(this.$refs.lightForm)
  },
  watch: {
    'data.w': function (val) {
      if (val > 1210 && Object.values(this.grainDirection)[0] === 'Horizontal Grain') {
        this.data.opposingGrain = false // cant be vert
      } else if (val > 1210 && Object.values(this.grainDirection)[0] !== 'Horizontal Grain') {
        this.data.opposingGrain = true // oppose vert or N/A, needs to be hoz
      }
    },
    'data.h': function (val) {
      if (val > 1210 && Object.values(this.grainDirection)[0] === 'Vertical Grain') {
        this.data.opposingGrain = false
      } else if (val > 1210 && Object.values(this.grainDirection)[0] !== 'Vertical Grain') {
        this.data.opposingGrain = true
      }
    },
    ventDialog(val) {
      if (val) {
        this.ventBackup = { vent: this.data.vent, ventOffset: this.data.ventOffset, ventRef: this.data.ventRef }
      }
    },
    //watch rules in this.$refs.form
    async lightingDialog() {
      if (this.$refs.form && !(await this.$utils.validateForm(this.$refs.form))) this.$refs.form.reset()
    },
    edgeDialog(val) {
      // Initialize edge data when dialog opens for backwards compatibility
      if (val && this.hasEdgeDialog) {
        if (!this.data.edges) {
          this.data.edges = { top: false, bottom: false, left: false, right: false }
        }
      }
    }
  },
  computed: {
    noLightingCoverPanel() {
      return !['underside', 'shelf'].includes(this.data.coverPanelType)
    },
    usJumboSheet() {
      return this.usLongMax + this.twoInchesInMM
    },
    usNormalSheet() {
      return this.usShortMax + this.twoInchesInMM
    },
    longLengthMax() {
      const m = this.materials.find((m) => {
        return m.value === this.data.material
      })

      if (this.region === 'us' && this.data.coverPanelType && m) {
        if (m.w === this.usJumboSheet && this.shippingZone === 'San Francisco') {
          return this.usLongMax
        } else {
          return this.usShortMax
        }
      } else if (this.region === 'uk' && this.data.coverPanelType && m) {
        return this.data.coverPanelType === 'plinth' ? m.w : m.w - 20
      } else if (this.region === 'eu' && this.data.coverPanelType && m) {
        return this.euLongMax
      } else {
        return 0
      }
    },
    shortLengthMax() {
      const m = this.materials.find((m) => {
        return m.value === this.data.material
      })
      if (!m) {
        return 0
      } else {
        return this.data.coverPanelType === 'plinth' ? m.h : m.h - 20
      }
    },
    hasGrain() {
      const m = this.materials.find((m) => {
        return m.value === this.data.material
      })
      return m?.hasGrain || false
    },
    filteredMaterials() {
      return this.materials.filter((material) => {
        return material.thickness >= 18
      })
    },
    lacquerCheckDisabled() {
      let val = false
      switch (this.data.coverPanelType) {
        case 'v-plinth':
        case 'plinth':
        case 'shelf':
          val = true
          break
      }
      return val
    },
    ventDialogDisabled() {
      let val = true
      switch (this.data.coverPanelType) {
        case 'v-plinth':
          val = false
          break
      }
      return val
    },
    tips() {
      let tips = {}

      this.materials.forEach((material) => {
        if (this.region === 'us' && this.data.coverPanelType) {
          if (material.w === this.usJumboSheet && this.shippingZone === 'San Francisco') {
            tips[material.value] = productData.getCoverPanelType(this.data.coverPanelType).longMaxTip
          } else {
            tips[material.value] = productData.getCoverPanelType(this.data.coverPanelType).shortMaxTip
          }
        }
      })

      return tips
    },
    onlyHozAllowed() {
      if (this.data.w > 1210 && Object.values(this.grainDirection)[0] === 'Horizontal Grain') {
        return true // must disable
      } else if (this.data.w > 1210 && Object.values(this.grainDirection)[0] !== 'Horizontal Grain') {
        return true // must disable
      } else {
        return false // else narrow
      }
    },
    onlyVertAllowed() {
      if (this.data.h > 1210 && Object.values(this.grainDirection)[0] === 'Vertical Grain') {
        return true
      } else if (this.data.h > 1210 && Object.values(this.grainDirection)[0] !== 'Vertical Grain') {
        return true
      } else {
        return false
      }
    },
    grainRequired() {
      if (!this.hasGrain) {
        return false
      } else {
        return true
      }
    },
    hasEdgeDialog() {
      return [
        'end-b',
        'end-w',
        'end-b-l',
        'end-b-r',
        'end-w-l',
        'end-w-r',
        'island',
        'back',
        'top',
        'underside',
        'shelf'
      ].includes(this.data.coverPanelType)
    },
    edgeBandingEnabled: {
      get() {
        return this.data.edgeMode === 'edgeBanding'
      },
      set(val) {
        this.data.edgeMode = val ? 'edgeBanding' : null
        // Reset all edge checkboxes when edge-banding is turned off
        if (!val && this.data.edges) {
          this.data.edges.top = false
          this.data.edges.bottom = false
          this.data.edges.left = false
          this.data.edges.right = false
        }
      }
    },
    hasLightingSet() {
      return this.data.lighting != null && this.data.lighting !== ''
    },
    hasOpposingGrainSet() {
      return this.data.opposingGrain === true
    },
    hasVentSet() {
      return (
        this.data.vent != null &&
        this.data.vent !== '' &&
        this.data.ventRef != null &&
        this.data.ventOffset != null &&
        this.data.ventOffset !== ''
      )
    },
    hasEdgesSet() {
      return (
        this.data.edges &&
        (this.data.edges.top || this.data.edges.bottom || this.data.edges.left || this.data.edges.right)
      )
    },
    lacquerGuides() {
      const min = 400 // minWidthForLacquer / minLacquerSize
      const guides = { left: false, right: false, top: false, bottom: false }

      switch (this.data.coverPanelType) {
        case 'end-b':
        case 'end-w':
        case 'island':
          if (this.data.w > min) {
            guides.left = true
            guides.right = true
          }
          break
        case 'end-b-l':
        case 'end-w-l':
          if (this.data.w > min) guides.right = true
          break
        case 'end-b-r':
        case 'end-w-r':
          if (this.data.w > min) guides.left = true
          break
        case 'top':
        case 'underside':
          // <400 deep is fully finished (no guide line); >=400 gets a guide on the top edge
          if (this.data.h >= min) guides.top = true
          break
      }

      return guides
    },
    hasLacquerGuide() {
      return this.lacquerGuides.left || this.lacquerGuides.right || this.lacquerGuides.top || this.lacquerGuides.bottom
    }
  },
  methods: {
    async saveVentDialog() {
      if (await this.$utils.validateForm(this.$refs.ventForm)) {
        this.ventDialog = false
      }
    },
    cancelVentDialog() {
      this.data.vent = null
      this.data.ventOffset = null
      this.data.ventRef = null
      this.ventBackup = null
      this.ventDialog = false
      this.$refs.ventForm.resetValidation()
    },
    onTypeChange(val) {
      switch (val) {
        case 'v-plinth':
          this.data.vent = null
          this.data.ventOffset = null
          this.data.ventRef = null
          break
        case 'plinth':
        case 'shelf':
          this.data.doubleSided = true
          break
        default:
          this.data.doubleSided = false
      }

      // Initialize edge data for applicable types
      if (
        [
          'end-b',
          'end-w',
          'end-b-l',
          'end-b-r',
          'end-w-l',
          'end-w-r',
          'island',
          'back',
          'top',
          'underside',
          'shelf'
        ].includes(val)
      ) {
        if (!this.data.edges) {
          this.data.edges = { top: false, bottom: false, left: false, right: false }
        }
      } else {
        // Clear edge data for non-applicable types
        this.data.edges = null
        this.data.edgeMode = null
      }
    },
    async getShippingZoneName(loc) {
      const shippingZoneSnap = await getDoc(doc(db, 'shippingZones', loc))

      if (shippingZoneSnap.exists()) {
        this.shippingZone = shippingZoneSnap.data().name
      }
    },
    findCoverPanelHex(materialCode) {
      if (!materialCode) return []
      const match = this.materials.find((material) => {
        return material.value === materialCode
      })
      return match.hex
    }
  }
}
</script>

<style lang="scss" scoped>
.row {
  border-bottom: solid 1px #eee;
  .col {
    padding-top: 0px;
    padding-bottom: 0px;
  }
}
.colour {
  min-width: 25px;
  width: 25px;
  height: 25px;
  border: solid 1px #999;
  border-radius: 3px;
  margin-right: 12px;
}
.edge-box {
  position: relative;
  width: 200px;
  height: 120px;
  border: solid grey 1px;
  margin: 30px auto 40px;
  display: flex;
  align-items: center;
  justify-content: center;

  .edge-label {
    font-size: 12px;
    color: #666;
  }

  .v-checkbox {
    position: absolute;
    margin: 0;
    padding: 0;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .edge-top {
    top: 0;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .edge-bottom {
    bottom: 0;
    left: 50%;
    transform: translate(-50%, 50%);
  }

  .edge-left {
    left: 0;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  .edge-right {
    right: 0;
    top: 50%;
    transform: translate(50%, -50%);
  }

  .lacquer-guide {
    position: absolute;
    pointer-events: none;
    z-index: 0;
  }

  .guide-left {
    left: 24px;
    top: 14px;
    bottom: 14px;
    border-left: 2px dashed #e53935;
  }

  .guide-right {
    right: 24px;
    top: 14px;
    bottom: 14px;
    border-left: 2px dashed #e53935;
  }

  .guide-top {
    top: 18px;
    left: 14px;
    right: 14px;
    border-top: 2px dashed #e53935;
  }

  .guide-bottom {
    bottom: 18px;
    left: 14px;
    right: 14px;
    border-top: 2px dashed #e53935;
  }

  .dim-width {
    position: absolute;
    bottom: -35px;
    left: 50%;
    transform: translateX(-50%);
    font-size: 11px;
  }

  .dim-height {
    position: absolute;
    right: -70px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 11px;
  }
}
.lacquer-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  font-size: 12px;
  color: #666;
  margin-top: 8px;

  .lacquer-swatch {
    display: inline-block;
    width: 22px;
    border-top: 2px dashed #e53935;
    margin-right: 8px;
  }
}
</style>
